import { useLayoutEffect } from 'react'

const TOP_BANNER_OFFSET_VARIABLE = '--top-banner-offset'

const decodeHash = (hash: string) => {
  try {
    return decodeURIComponent(hash)
  } catch {
    return hash
  }
}

// 画面上部に固定表示される環境表示バナー・お知らせバナーの高さ。
// layouts/application.html.erb が body の CSS 変数として出している。
const topBannerOffset = () => {
  const value = window
    .getComputedStyle(document.body)
    .getPropertyValue(TOP_BANNER_OFFSET_VARIABLE)
  const offset = Number.parseFloat(value)

  return Number.isFinite(offset) ? offset : 0
}

/**
 * URL のフラグメント（`#xxx`）が指す要素までスクロールする
 *
 * ERB が空の `#react_container` だけを返し、中身を React が描画する画面では、
 * ブラウザがフラグメントを解決する時点で対象要素がまだ存在しないため、標準の
 * アンカースクロールが働かない。React 18 以降の `createRoot()` はコミットが
 * 非同期であり、待ち時間の調整では解決できないため、マウント後に自前でスクロールする。
 * useEffect だとコミット後にページ先頭で1フレーム描画されてからジャンプするため、
 * React コミット後の最初のペイント前に位置を確定できる useLayoutEffect を使う
 * （バンドル実行完了までの間に静的 HTML の先頭が見えるのは対象外）。
 * サイト全体に `scroll-behavior: smooth`（common/_base.scss）が効いており、
 * behavior 未指定だとアニメーションになって位置が同期確定しないため `instant` を明示する。
 *
 * 対象要素より上に高さの決まっていない画像があると読み込み完了後に位置がずれるので、
 * ユーザーが自分でスクロールしていない場合に限り、位置を補正し直す。補正のトリガーは
 * window `load` ではなく未ロード画像ごとの load/error にする。window `load` は無関係な
 * リソースの完了まで補正を遅らせるうえ、キャッシュ済み再訪では React のコミットより
 * 先に発火してしまい（readyState === 'complete'）、補正の機会自体を失うため。
 * 操作検知に touchstart / pointerdown を使わないのも意図的で、これらは単なるタップでも
 * 発火し、スクロール位置を変えていないのに補正を放棄してしまう。
 * ただし iOS の WebKit はわずかな指の動きでも touchmove を配送するため、ジッターを
 * 伴うタップが補正を放棄するケースは残る（既知の制限として許容する）。
 */
export const useScrollToHash = () => {
  useLayoutEffect(() => {
    const hash = window.location.hash.slice(1)
    if (hash === '') return

    const id = decodeHash(hash)
    const scrollToTarget = () => {
      const target = document.getElementById(id)
      if (target === null) return

      window.scrollTo({
        top:
          target.getBoundingClientRect().top +
          window.scrollY -
          topBannerOffset(),
        behavior: 'instant',
      })
    }

    scrollToTarget()

    // この時点で React 挿入済みの画像も document.images に含まれる。
    // これらの読み込み完了がレイアウトを押し広げ、着地点をずらす。
    const pendingImages = Array.from(document.images).filter(
      (img) => !img.complete,
    )
    if (document.readyState === 'complete' && pendingImages.length === 0) {
      return
    }

    let userOperated = false
    const markUserOperated = () => {
      userOperated = true
    }
    const correctScroll = () => {
      if (userOperated) return

      scrollToTarget()
    }

    const userEventTypes = ['wheel', 'touchmove', 'keydown']
    userEventTypes.forEach((type) => {
      window.addEventListener(type, markUserOperated, { passive: true })
    })
    pendingImages.forEach((img) => {
      img.addEventListener('load', correctScroll)
      img.addEventListener('error', correctScroll)
    })
    if (document.readyState !== 'complete') {
      window.addEventListener('load', correctScroll)
    }

    return () => {
      userEventTypes.forEach((type) => {
        window.removeEventListener(type, markUserOperated)
      })
      pendingImages.forEach((img) => {
        img.removeEventListener('load', correctScroll)
        img.removeEventListener('error', correctScroll)
      })
      window.removeEventListener('load', correctScroll)
    }
  }, [])
}
